import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  accountQuery,
  brandOriginUrl,
  buildCasinoGameFilters,
  casinoGamesQuery,
  useAuthSession,
} from '@/api';
import {
  casinoGameThumbUrl,
  casinoNavSlug,
  casinoVendorLogoUrl,
  type CasinoGame,
} from '@/domain';
import { cn } from '@/lib/cn';
import { useIsDesktop } from '@/lib/useMediaQuery';
import { Button, Icon, Spinner, buttonVariants } from '@/ui/common';
import { GameTile } from './GameTile';
import {
  LatestWins,
  LIST_MAX_H_DETAILS_DESKTOP,
  VISIBLE_COUNT_DETAILS_DESKTOP,
} from './LatestWins';
import { useCasinoFavorites } from './useCasinoFavorites';

function formatLocalClock(date: Date): string {
  const hh = String(date.getHours()).padStart(2, '0');
  const mm = String(date.getMinutes()).padStart(2, '0');
  const ss = String(date.getSeconds()).padStart(2, '0');
  return `${hh}:${mm}:${ss}`;
}

function FullscreenIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={16}
      height={16}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden
    >
      <path d="M8 3H5a2 2 0 0 0-2 2v3" />
      <path d="M16 3h3a2 2 0 0 1 2 2v3" />
      <path d="M8 21H5a2 2 0 0 1-2-2v-3" />
      <path d="M16 21h3a2 2 0 0 0 2-2v-3" />
    </svg>
  );
}

const iconToolbarBtnClass = cn(
  buttonVariants({ variant: 'secondary', size: 'sm' }),
  '!min-h-8 !w-8 !shrink-0 !gap-0 !px-0 !py-0',
);

/** Related tiles in game details — 3×3 on desktop. */
const RELATED_GAMES_DESKTOP = 9;
/** Mobile stacked column. */
const RELATED_GAMES_MOBILE = 6;

export interface GameDetailsPanelProps {
  title: string;
  provider: string;
  vendor: string;
  gameTag: string;
  category: string;
  game?: CasinoGame | null;
  playLoading?: boolean;
  playError?: string | null;
  /** When set, Demo/Play CTAs are replaced by an inline game iframe. */
  gameUrl?: string | null;
  /** Desktop: swap CTAs for the player shell (loading / error / iframe). */
  playing?: boolean;
  /** Taller iframe for live casino. */
  tallPlayer?: boolean;
  onPlay: () => void;
  onBack: () => void;
  /** Clear the iframe and show Demo/Play again. */
  onStopPlay?: () => void;
  onRetryPlay?: () => void;
  onOpenWindow?: () => void;
  className?: string;
}

const STAT_PLACEHOLDERS = [
  { label: 'Live RTP (%)', value: '—', live: true },
  { label: 'Real RTP (%)', value: '—' },
  { label: 'Volatility', value: '—' },
  { label: 'Max Win', value: '—' },
] as const;

export function GameDetailsPanel({
  title,
  provider,
  vendor,
  gameTag,
  category,
  game = null,
  playLoading = false,
  playError = null,
  gameUrl = null,
  playing = false,
  tallPlayer = false,
  onPlay,
  onBack,
  onStopPlay,
  onRetryPlay,
  onOpenWindow,
  className,
}: GameDetailsPanelProps) {
  const isDesktop = useIsDesktop();
  const { user } = useAuthSession();
  const { data: account } = useQuery(accountQuery());
  const { showFavorite, toggleFavorite } = useCasinoFavorites();
  const relatedLimit = isDesktop ? RELATED_GAMES_DESKTOP : RELATED_GAMES_MOBILE;
  const isPlaying = playing || Boolean(gameUrl);
  const playerShellRef = useRef<HTMLDivElement | null>(null);
  const [clock, setClock] = useState(() => formatLocalClock(new Date()));

  useEffect(() => {
    if (!isPlaying) return;
    setClock(formatLocalClock(new Date()));
    const id = window.setInterval(() => {
      setClock(formatLocalClock(new Date()));
    }, 1000);
    return () => window.clearInterval(id);
  }, [isPlaying]);

  const onFullscreen = () => {
    const node = playerShellRef.current;
    if (!node) return;
    if (document.fullscreenElement === node) {
      void document.exitFullscreen?.();
      return;
    }
    void node.requestFullscreen?.();
  };

  const displayGame: CasinoGame = game ?? {
    Name: title,
    GameTag: gameTag,
    ProviderName: provider,
    VendorName: vendor,
    ProviderGameId: 0,
    CategoryName: category,
  };

  const thumb = casinoGameThumbUrl(brandOriginUrl(), displayGame);
  const vendorLabel = displayGame.VendorName || vendor;
  const providerLabel = displayGame.ProviderName || provider;
  const logoSrc = casinoVendorLogoUrl(brandOriginUrl(), vendorLabel);

  const relatedFilters = useMemo(
    () =>
      buildCasinoGameFilters({
        account,
        user,
        category,
        vendorOrFlag: vendorLabel,
        liveMode: casinoNavSlug(category) === 'livecasino',
      }),
    [account, user, category, vendorLabel],
  );

  const relatedQuery = useQuery({
    ...casinoGamesQuery(relatedFilters, relatedLimit + 1),
    enabled: Boolean(relatedFilters.AccountGuid),
  });

  const relatedGames = useMemo(() => {
    const tag = casinoNavSlug(gameTag);
    return (relatedQuery.data?.Data ?? [])
      .filter((row) => casinoNavSlug(row.GameTag) !== tag)
      .slice(0, relatedLimit);
  }, [relatedQuery.data, gameTag, relatedLimit]);

  const canFavorite = showFavorite && displayGame.ProviderGameId > 0;
  const isFavorite = displayGame.FavoriteId != null;

  return (
    <div
      className={cn(
        'mx-auto flex w-full max-w-[720px] flex-col gap-4 lg:max-w-[960px]',
        className,
      )}
    >
      <button
        type="button"
        onClick={onBack}
        className="flex size-[18px] shrink-0 items-center justify-center text-icon-default"
        aria-label="Back"
      >
        <Icon name="arrow-right" size={18} className="-scale-x-100" />
      </button>

      <section className="flex w-full flex-col gap-3 overflow-hidden rounded-md border border-border-subtle bg-bg-elevated p-3">
        <div className="flex w-full items-center gap-3">
          <div className="h-[85px] w-16 shrink-0 overflow-hidden rounded-md bg-bg-surface">
            <img
              src={thumb}
              alt=""
              className="size-full object-cover"
              onError={(event) => {
                event.currentTarget.style.visibility = 'hidden';
              }}
            />
          </div>
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            <h1 className="w-full text-[18px] font-bold leading-normal text-text-primary">
              {title}
            </h1>
            <div className="flex w-full items-center gap-2">
              <div className="relative flex h-8 w-16 shrink-0 items-center justify-center overflow-hidden">
                <img
                  src={logoSrc}
                  alt={providerLabel}
                  className="max-h-8 max-w-full object-contain"
                  onError={(event) => {
                    event.currentTarget.style.display = 'none';
                    const parent = event.currentTarget.parentElement;
                    if (parent && !parent.querySelector('[data-provider-fallback]')) {
                      const span = document.createElement('span');
                      span.dataset.providerFallback = '1';
                      span.className =
                        'text-[10px] uppercase tracking-wide text-text-secondary';
                      span.textContent = providerLabel;
                      parent.appendChild(span);
                    }
                  }}
                />
              </div>
              {canFavorite ? (
                <button
                  type="button"
                  className={cn(
                    'flex size-8 shrink-0 items-center justify-center rounded-md border border-border-default bg-bg-surface p-2 text-text-primary',
                    isFavorite && 'text-action-primary',
                  )}
                  aria-label={isFavorite ? 'Remove favourite' : 'Add favourite'}
                  onClick={() => toggleFavorite(displayGame)}
                >
                  <Icon name="star" size={14} />
                </button>
              ) : null}
            </div>
          </div>
        </div>

        <div className="flex w-full items-center justify-between">
          {STAT_PLACEHOLDERS.flatMap((stat, index) => {
            const column = (
              <div
                key={stat.label}
                className="flex min-w-0 flex-1 flex-col items-center gap-1 overflow-hidden"
              >
                <div className="flex items-center gap-1.5">
                  {'live' in stat && stat.live ? (
                    <span className="size-1.5 shrink-0 rounded-full bg-status-success" aria-hidden />
                  ) : null}
                  <p className="whitespace-nowrap text-sm font-bold text-text-primary">
                    {stat.value}
                  </p>
                </div>
                <p className="whitespace-nowrap text-[10px] text-text-secondary">{stat.label}</p>
              </div>
            );
            if (index === 0) return [column];
            return [
              <div
                key={`divider-${stat.label}`}
                className="h-7 w-px shrink-0 bg-border-subtle"
                aria-hidden
              />,
              column,
            ];
          })}
        </div>
      </section>

      {isPlaying ? (
        <section className="flex w-full flex-col overflow-hidden rounded-md border border-border-subtle bg-bg-elevated">
          <div className="flex h-12 items-center justify-between gap-3 border-b border-border-default px-3">
            <p className="min-w-0 truncate text-body-s font-bold text-text-primary">{title}</p>
            <div className="flex shrink-0 items-center gap-2">
              {onOpenWindow && gameUrl ? (
                <button
                  type="button"
                  className={iconToolbarBtnClass}
                  aria-label="Open window"
                  title="Open window"
                  onClick={onOpenWindow}
                >
                  <Icon name="monitor-play" size={16} />
                </button>
              ) : null}
              <button
                type="button"
                className={iconToolbarBtnClass}
                aria-label="Fullscreen"
                title="Fullscreen"
                onClick={onFullscreen}
                disabled={!gameUrl}
              >
                <FullscreenIcon />
              </button>
              {onStopPlay ? (
                <button
                  type="button"
                  className={iconToolbarBtnClass}
                  aria-label="Close"
                  title="Close"
                  onClick={onStopPlay}
                >
                  <Icon name="close" size={16} />
                </button>
              ) : null}
            </div>
          </div>
          <div
            ref={playerShellRef}
            className={cn(
              'relative w-full overflow-hidden bg-bg-canvas',
              tallPlayer ? 'min-h-[70vh]' : 'aspect-video',
            )}
          >
            {playLoading && !gameUrl ? (
              <div className="absolute inset-0 flex items-center justify-center">
                <Spinner label="Starting game" />
              </div>
            ) : playError && !gameUrl ? (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-6 text-center">
                <p className="text-body-m text-status-danger">{playError}</p>
                {onRetryPlay ? (
                  <Button variant="secondary" onClick={onRetryPlay}>
                    Try again
                  </Button>
                ) : null}
                {onStopPlay ? (
                  <Button variant="ghost" onClick={onStopPlay}>
                    Back
                  </Button>
                ) : null}
              </div>
            ) : gameUrl ? (
              <iframe
                title={title}
                src={gameUrl}
                className="size-full border-0"
                allowFullScreen
                allow="autoplay; fullscreen; payment; clipboard-write"
                referrerPolicy="no-referrer-when-downgrade"
              />
            ) : null}
          </div>
          <div className="flex h-9 items-center justify-end border-t border-border-default px-3">
            <span className="font-mono text-caption tabular-nums text-text-secondary" aria-live="polite">
              {clock}
            </span>
          </div>
        </section>
      ) : (
        <>
          <div className="flex w-full items-center justify-center gap-2">
            <Button variant="secondary" size="lg" fullWidth disabled className="min-w-0 flex-1">
              Demo Play
            </Button>
            <Button
              variant="primary"
              size="lg"
              fullWidth
              className="min-w-0 flex-1"
              loading={playLoading}
              onClick={onPlay}
            >
              Play Now!
            </Button>
          </div>

          {playError ? (
            <p className="text-center text-body-s text-status-danger" role="alert">
              {playError}
            </p>
          ) : null}
        </>
      )}

      <div className="flex w-full flex-col gap-4 lg:grid lg:grid-cols-2 lg:gap-6">
        <LatestWins
          showSeeAll={false}
          compact={!isDesktop}
          listMaxHeight={isDesktop ? LIST_MAX_H_DETAILS_DESKTOP : undefined}
          visibleCount={isDesktop ? VISIBLE_COUNT_DETAILS_DESKTOP : undefined}
        />

        <section className="flex w-full flex-col gap-3">
          <h2 className="w-full text-base font-bold leading-normal text-text-primary">
            Other games from {vendorLabel}
          </h2>
          {relatedQuery.isLoading && relatedGames.length === 0 ? (
            <div className="flex h-28 items-center justify-center">
              <Spinner label="Loading games" />
            </div>
          ) : relatedGames.length === 0 ? null : (
            <div className="flex flex-wrap content-start gap-3">
              {relatedGames.map((row) => (
                <div key={`${row.ProviderGameId}-${row.GameTag}`} className="w-[111px]">
                  <GameTile
                    game={row}
                    categoryOverride={category}
                    size="sm"
                    showFavorite={showFavorite}
                    onToggleFavorite={toggleFavorite}
                  />
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
